import type { ReactNode } from 'react';
import styled from 'styled-components';

interface Props {
  /** Email of the signed-in account (e.g. Clerk `useUser().primaryEmailAddress`). */
  readonly email?: string;
  readonly onLogout: () => void;
  readonly children: ReactNode;
}

/**
 * Tiny authenticated frame around the terminal: the signed-in email on the
 * top-left and a "Log Out" control on the top-right. Rendered by
 * `DimeCloudApp` on the signed-in path; also exported for hosts composing
 * their own shell. Styles read the host's CSS variables (`--border-color`,
 * `--text-secondary`, …).
 */
export function AuthShell({ email, onLogout, children }: Props): JSX.Element {
  return (
    <Shell>
      <Bar>
        <Email title={email}>{email ?? 'Signed in'}</Email>
        <LogOut type="button" onClick={onLogout}>
          Log Out
        </LogOut>
      </Bar>
      <Body>{children}</Body>
    </Shell>
  );
}

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Bar = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--border-color);
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const Email = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LogOut = styled.button`
  all: unset;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 5px;
  color: var(--text-label);

  &:hover {
    color: var(--text-primary);
    background: var(--hover-bg);
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
`;
